import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { emptyTeachingObj } from '@app/account/profile/user-profile.variables';
import { DictionaryService } from '@app/core/services/bff/dictionary.service';
import { findSubjectById } from '@app/core/services/bff/helpers.service';
import { UserProfileBffService, UserProfileInfo } from '@app/core/services/bff/user-profile.service';
import { ChatService } from '@app/core/services/chat.service';
import { ReviewsBlockType } from '@app/teachers/teacher-card/teacher-card-variables';
import { LoadingBarService } from '@core/components/spinners/loading-bar/loading-bar.service';
import { vimeoEmbedUrl, youtubeEmbedUrl } from '@core/helpers/video';
import {
  AccountService,
  AuthService,
  CommentService,
  ContextService,
  FeaturesService,
  FilesService,
  IFileType,
  LanguageService,
  MetaService,
  ProfileService,
} from '@core/services';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import {
  EContentPanelRoute,
  FeatureEnum,
  ITeachingSubject,
  Language,
  MediaFormatEnum,
  ProfileMediaModeEnum,
  Subject,
  SubjectCategoryEnum,
  TenantEnum,
  User,
  UserProfile,
  UserStatusEnum,
} from 'lingo2-models';
import { trimStart, uniqBy } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, finalize, Observable } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BaseUserProfileSubpageComponent } from '../base-user-profile-subpage.component';

interface ITeachingSubjectItem {
  value: string | number;
  title: string;
  subject: ITeachingSubject;
}

@Component({
  selector: 'app-user-profile-teaching-subpage',
  templateUrl: './user-profile-teaching-subpage.component.html',
  styleUrls: ['./user-profile-teaching-subpage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileTeachingSubpageComponent extends BaseUserProfileSubpageComponent implements OnInit {
  @Input()
  public set selectedSubjectId(selectedSubjectId: string) {
    this._selectedSubjectId = selectedSubjectId;
    console.log('[TeachingSubpage] selectedSubjectId', selectedSubjectId);
    this.init();
  }

  public get selectedSubjectId(): string {
    return this._selectedSubjectId;
  }

  @Input()
  public set me(me: User) {
    if (!me) {
      return;
    }
    this._me = me;
  }

  @Input()
  public set userProfile(userProfile: UserProfileInfo) {
    if (!userProfile) {
      return;
    }
    this._profile = userProfile;
    this.prepareCardByProfile();
  }

  @Input()
  public set user(user: User) {
    if (!user) {
      return;
    }
    this._user = user;
    this.loadMeetingReviews(this.latestMeetingReviews);
    this.prepareCardByProfile();
  }

  @Output() public onChangeSubject = new EventEmitter<string>();
  @Output() public isCloseAllModals = new EventEmitter<boolean>();
  @Output() public loaded = new EventEmitter<boolean>();

  public teachingSubjectsList: ITeachingSubjectItem[] = [];
  public isMediaPrepared = false;
  public isVideoFromYoutube = false;
  public _subjects: Subject[] = [];
  public languages: Language[] = [];
  public teachingSubject: ITeachingSubject;
  public safeMediaUrl: SafeResourceUrl;
  public surfaceBlockDisplay: boolean;
  public routes = EContentPanelRoute;
  public about: string;

  private _selectedSubjectId: string;
  private subjectId: string;

  public emptyTeachingObj = emptyTeachingObj;

  public latestMeetingReviews: ReviewsBlockType = {
    items: [],
    loading: false,
    loaded: false,
    filter: {},
    pagination: {
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 1,
    },
    order: [['updatedAt', 'DESC']],
  };

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    protected router: Router,
    protected authService: AuthService,
    protected contextService: ContextService,
    protected profileService: ProfileService,
    protected features: FeaturesService,
    protected meta: MetaService,
    protected cdr: ChangeDetectorRef,
    protected userProfileBff: UserProfileBffService,
    protected dictionaryService: DictionaryService,
    protected languageService: LanguageService,
    public deviceService: DeviceDetectorService,
    protected loadingBar: LoadingBarService,
    private chatService: ChatService,
    private commentService: CommentService,
    private sanitizer: DomSanitizer,
    protected filesService: FilesService,
  ) {
    super(
      router,
      authService,
      contextService,
      profileService,
      dictionaryService,
      languageService,
      userProfileBff,
      features,
      meta,
      loadingBar,
      cdr,
    );
    this.loadUserData$.pipe(takeUntil(this.destroyed$)).subscribe();
    this.getSubjectId$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  public init(): void {
    console.log('[TeachingSubpage] init', this.subjectId, this.loading);

    if (this.loading) {
      return;
    }

    this.loading = true;
    this.subjectId = this.selectedSubjectId ? this.selectedSubjectId : this.parseSubjectIdFromUrl(this.router.url);

    this.initTeachingSubject();
    this.prepareCardByProfile();
    this.loaded.emit(true);
    this.detectChanges();
  }

  public ngOnInit(): void {
    this.init();
  }

  private loadMeetingReviews(meetingReviewsBlockType: ReviewsBlockType) {
    if (!this._user) {
      return;
    }
    this.commentService
      .findReviews('teacher', this._user.id, meetingReviewsBlockType.pagination, meetingReviewsBlockType.order, true)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          meetingReviewsBlockType.items = response.results;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected prepareCardByProfile() {
    if (!this._user || !this._profile || !this.subjects.length) {
      return;
    }

    this.about = this._profile.about;
    this.accountRoute = AccountService.accountRoute(this._user).join('/');
    this.teachingSubjectsList = (this._profile.teachingSubjects || [])
      .filter((s) => s.subjectId > 0)
      .map((subject, index) => {
        const _subject = this.subjects.find((s) => s.id === +subject.subjectId);
        return {
          value: index,
          title: (_subject ? _subject.title : null) || '---',
          route: AccountService.accountRoute(this._user, ['teaching', subject.subjectId.toString()]),
          native: findSubjectById(this._subjects, subject)?.category_id === SubjectCategoryEnum.native_language,
          subject: {
            subject_id: subject.subjectId,
            subject: null,
            subject_other_name: null,
            subject_other_id: null,
            description: subject.description,
            video_url: subject.videoUrl,
            video_id: subject.videoId,
            video: null,
            video_mode: subject.videoMode,
          },
        };
      });

    // Убираем дубли по ID дисциплины из массива дисциплин
    this.teachingSubjectsList = uniqBy(this.teachingSubjectsList, (e) => e.subject.subject_id);
    this.initTeachingSubject();

    this.isMe = this._me && this._user && this._me?.id === this._user.id;
    if (!(AccountService.isAsIfTeacher(this._user) || AccountService.isTeacher(this._user))) {
      this.router.navigate(AccountService.accountRoute(this._user, ['about'])).then();
    }
    this.isGuest = this._me && this._me.status === UserStatusEnum.guest;
    this.loading = false;
  }

  public haveTeachingSubjects() {
    return !!this.teachingSubjectsList.length;
  }

  protected initTeachingSubject() {
    if (!this.teachingSubjectsList.length) {
      return;
    }

    console.log('[TeachingSubpage] initTeachingSubject', this.subjectId);

    if (this.subjectId) {
      const item = this.teachingSubjectsList.find((s) => s.subject.subject_id.toString() == this.subjectId);
      this.teachingSubject = item ? item.subject : null;
      this.detectChanges();
    } else {
      this.teachingSubject = this.teachingSubjectsList[0]?.subject;
    }
    this.prepareMedia();
  }

  protected pickMedalUrl(file: IFileType): string {
    return file.media.find((_file) => _file.format === MediaFormatEnum.mp4)?.url;
  }

  public prepareMedia() {
    const teachingSubject = this.teachingSubject;
    if (!teachingSubject || (!teachingSubject.video_url && !teachingSubject.video_id)) {
      this.safeMediaUrl = null;
      this.isMediaPrepared = false;
      return;
    }
    if (
      teachingSubject.video_id &&
      (teachingSubject.video_mode[0] || teachingSubject.video_mode) === ProfileMediaModeEnum.use_media_id
    ) {
      this.safeMediaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        FilesService.getFileUrlBySize(teachingSubject.video_id),
      );
      this.isVideoFromYoutube = false;
      this.isMediaPrepared = true;
      this.detectChanges();
    } else if (
      teachingSubject.video_url &&
      (teachingSubject.video_mode[0] || teachingSubject.video_mode) === ProfileMediaModeEnum.use_media_url
    ) {
      this.safeMediaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        youtubeEmbedUrl(teachingSubject.video_url) || vimeoEmbedUrl(teachingSubject.video_url),
      );
      this.isMediaPrepared = true;
      this.isVideoFromYoutube = true;
      this.detectChanges();
    } else {
      this.safeMediaUrl = null;
      this.isMediaPrepared = false;
    }
  }

  public onOpenChat() {
    this.chatService.openPrivateChat(this._user?.id);
  }

  public onIinViewportChange(e) {
    this.surfaceBlockDisplay = e;
    this.markForCheck();
  }

  public setSubjectId(subjectId: string) {
    if (this._selectedSubjectId === subjectId) {
      return;
    }
    this._selectedSubjectId = subjectId;
    this.onChangeSubject.emit(subjectId);
    this.init();
  }

  private get getSubjectId$(): Observable<string> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => {
        this.subjectId = this.selectedSubjectId ? this.selectedSubjectId : this.parseSubjectIdFromUrl(event.url);
        return this.subjectId;
      }),
    );
  }

  public closeAllModals() {
    this.isCloseAllModals.emit(true);
  }

  private parseSubjectIdFromUrl(url: string): string {
    const urlParts = trimStart(url, '/').split('/');
    return urlParts.length > 3 ? urlParts[3] : null;
  }

  public isAvailableReviews() {
    return this.features.isAvailable(FeatureEnum.profile_reviews);
  }

  public isAvailableServices() {
    return this.features.isAvailable(FeatureEnum.profile_services);
  }

  public isMeVersion() {
    return this.features.tenant === TenantEnum.onclass_me;
  }
}
